<script setup lang="ts">
import { ETooltip } from '@erpx/ui'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouterLink } from 'vue-router'

import AppIcon from '@/components/AppIcon.vue'
import FsdBadge from '@/components/FsdBadge.vue'
import { isExtraneous } from '@/nav/apps'
import { navLabel, type NavItem } from '@/nav/items'

/**
 * One sidebar destination: icon, label, the "Not in FSD" marker when the C15
 * audit flagged the screen, and the favourite star.
 *
 * In the icon-only rail the label is still in the link — visually hidden, so
 * the link keeps a real accessible name rather than relying on the tooltip
 * (which a screen reader may never hear) — and the FSD marker shrinks to a
 * dot over the icon, carrying its own hidden text.
 */

const props = defineProps<{
  item: NavItem
  active: boolean
  rail: boolean
  favourite: boolean
}>()

const emit = defineEmits<{ toggleFavourite: [id: string] }>()

const { t } = useI18n()

const label = computed(() => navLabel(props.item))
const flagged = computed(() => isExtraneous(props.item))
const tooltip = computed(() =>
  flagged.value ? t('shell.sidebar.flaggedTooltip', { name: label.value }) : label.value,
)
</script>

<template>
  <li class="sidenav__row">
    <ETooltip :content="tooltip" side="right" :disabled="!rail">
      <RouterLink
        class="sidenav__link"
        :class="{ 'is-active': active }"
        :to="item.to"
        :aria-current="active ? 'page' : undefined"
      >
        <AppIcon :name="item.icon" :size="15" class="sidenav__icon" />
        <span :class="rail ? 'sr-only' : 'sidenav__label'">{{ label }}</span>
        <FsdBadge v-if="flagged" :variant="rail ? 'dot' : 'pill'" :class="rail ? 'sidenav__dot' : 'sidenav__badge'" />
      </RouterLink>
    </ETooltip>

    <button
      v-if="!rail"
      type="button"
      class="sidenav__star"
      :class="{ 'is-on': favourite }"
      :aria-pressed="favourite"
      :aria-label="
        favourite
          ? t('shell.sidebar.removeFavourite', { name: label })
          : t('shell.sidebar.addFavourite', { name: label })
      "
      @click="emit('toggleFavourite', item.id)"
    >
      <AppIcon name="star" :size="13" />
    </button>
  </li>
</template>
